import {
  VULNERABILITY_STATUSES,
  VULNERABILITY_SEVERITIES,
  CRITICAL,
  HIGH,
  MEDIUM,
  LOW,
  NOTE,
  REPORTED,
  SUSPICIOUS,
  CONFIRMED,
  NOT_A_PROBLEM,
  REMEDIATED,
  FIXED,
  AUTO_REMEDIATED
} from '../shared'
import { AttackStatus } from '../types/attack-status'
import { VulnerabilitySeverityBreakdown } from '../types/vulnerability-severity-breakdown'
import { VulnerabilityStatusBreakdown } from '../types/vulnerability-status-breakdown'
import { ApplicationImportance } from '../types/application-importance'
import { License } from '../types/license'
import { Application } from '../types/application'
import { validIntlDate } from './shared'
import { MetadataValue } from '../types/metadata-value'
import { InvalidCustomFieldType } from '../types/invalid-custom-field'
import { AgentLanguageEnum } from '../queries/agent-features'
import { GradeValue } from '../../../graphql/schema/types/grade-value'

interface BuildMockAppProps {
  readonly name: string
  readonly language?: string
  readonly attackStatus?: AttackStatus
  readonly attackTotal?: number
  readonly vulnerabilityBreakdown?: VulnerabilitySeverityBreakdown
  readonly tags?: string[]
  readonly hasAssess?: boolean
  readonly hasAProtectedServer?: boolean
  readonly license?: License
  readonly url?: string
  readonly linesOfCode?: string
  readonly importance?: ApplicationImportance
  readonly technologies?: string[]
  readonly online?: boolean
  readonly lastActivity?: string
  readonly primary?: boolean
  readonly moduleCount?: number
  readonly violatedCompliancePolicies?: Array<{
    readonly id: string
    readonly name: string
    readonly enabled: boolean
  }>
  readonly customMetadataFields?: MetadataValue[]
  readonly invalidCustomMetadataFields?: InvalidCustomFieldType[]
  readonly archived?: boolean
  readonly scoreGrade?: GradeValue
}

function buildStatuses(n: number): VulnerabilityStatusBreakdown {
  let countUsed = false
  return VULNERABILITY_STATUSES.reduce(
    (statusBreakdown, currStatus) => {
      return {
        ...statusBreakdown,
        [currStatus]:
          countUsed === false
            ? (() => {
                countUsed = true
                return n
              })()
            : 0
      }
    },
    { total: n, open: n }
  )
}

function buildVulnBreakdownVariant(
  severityCounts: number[]
): VulnerabilitySeverityBreakdown {
  let severityIndex = 0
  const count = severityCounts.reduce((acc, curr) => {
    return acc + curr
  }, 0)
  return VULNERABILITY_SEVERITIES.reduce(
    (severityBreakdown, severity) => {
      return {
        ...severityBreakdown,
        [severity]: buildStatuses(severityCounts[severityIndex++])
      }
    },
    {
      [CRITICAL]: buildStatuses(1),
      [HIGH]: buildStatuses(1),
      [MEDIUM]: buildStatuses(1),
      [LOW]: buildStatuses(1),
      [NOTE]: buildStatuses(1),
      total: count,
      open: count
    }
  )
}

function buildMockApp({
  name,
  language,
  attackStatus,
  attackTotal,
  vulnerabilityBreakdown,
  tags = [],
  hasAssess = false,
  hasAProtectedServer = false,
  license,
  url,
  linesOfCode,
  importance,
  technologies,
  online = false,
  lastActivity = validIntlDate,
  primary = false,
  moduleCount,
  violatedCompliancePolicies = [],
  customMetadataFields = [],
  invalidCustomMetadataFields = [],
  archived = false
}: BuildMockAppProps): Application {
  return {
    id: 'id',
    name,
    language: language || AgentLanguageEnum.JAVA,
    attackStatus,
    attackTotal: attackTotal || 0,
    vulnerabilityBreakdown:
      vulnerabilityBreakdown || buildVulnBreakdownVariant([7, 7, 7, 7, 7]),
    tags,
    hasAssess,
    hasAProtectedServer,
    license: license || null,
    url: url || 'http://www.webgoat.com',
    linesOfCode: linesOfCode || '7000',
    importance: importance || 'MEDIUM',
    technologies: technologies || ['Spring'],
    online,
    lastActivity,
    primary,
    moduleCount: moduleCount ? moduleCount : primary ? 2 : 1,
    violatedCompliancePolicies,
    customMetadataFields,
    invalidCustomMetadataFields,
    archived,
    scoreGrade: 'A'
  }
}

/**
 * Mock Applications
 *
 * Please make the name descriptive of what case each app is mocked for.
 * Avoid explicitly mocking application properties that do not relate to the specific case.
 * The expectation is that this list will grow large. It is intended for use by the development team
 * and the UX team, as a means to ensure that each UX case is satisfied in a way that is easy to review.
 * We should not mock applications for cases that can be put into an automated test and that do not need
 * to be reviewed visually, or from a user perspective.
 */
export const mockApplications = [
  buildMockApp({
    name: 'Vuln Breakdown List',
    vulnerabilityBreakdown: {
      [CRITICAL]: {
        [REPORTED]: 1,
        [SUSPICIOUS]: 10,
        [CONFIRMED]: 100,
        [NOT_A_PROBLEM]: 1000,
        [REMEDIATED]: 9,
        [FIXED]: 99,
        [AUTO_REMEDIATED]: 999,
        open: 111,
        total: 2218
      },
      [HIGH]: {
        [REPORTED]: 1,
        [SUSPICIOUS]: 1,
        [CONFIRMED]: 1,
        [NOT_A_PROBLEM]: 1,
        [REMEDIATED]: 1,
        [FIXED]: 1,
        [AUTO_REMEDIATED]: 1,
        open: 3,
        total: 7
      },
      [MEDIUM]: {
        [REPORTED]: 1,
        [SUSPICIOUS]: 1,
        [CONFIRMED]: 1,
        [NOT_A_PROBLEM]: 1,
        [REMEDIATED]: 1,
        [FIXED]: 1,
        [AUTO_REMEDIATED]: 1,
        open: 3,
        total: 7
      },
      [LOW]: {
        [REPORTED]: 1,
        [SUSPICIOUS]: 1,
        [CONFIRMED]: 1,
        [NOT_A_PROBLEM]: 1,
        [REMEDIATED]: 1,
        [FIXED]: 1,
        [AUTO_REMEDIATED]: 1,
        open: 3,
        total: 7
      },
      [NOTE]: {
        [REPORTED]: 1,
        [SUSPICIOUS]: 1,
        [CONFIRMED]: 1,
        [NOT_A_PROBLEM]: 1,
        [REMEDIATED]: 1,
        [FIXED]: 1,
        [AUTO_REMEDIATED]: 1,
        open: 3,
        total: 7
      },
      open: 35,
      total: 35
    }
  }),
  buildMockApp({
    name: 'LicensedApp',
    license: {
      expiration: '1975-08-19T23:15:30.000Z'
    }
  }),
  buildMockApp({
    name: 'Protected App',
    hasAProtectedServer: true
  }),
  // Attack Statuses
  buildMockApp({
    name: 'ExploitedApp',
    attackStatus: 'EXPLOITED',
    attackTotal: 10
  }),
  buildMockApp({
    name: 'ProbedApp',
    attackStatus: 'PROBED',
    attackTotal: 100
  }),
  buildMockApp({
    name: 'BlockedApp',
    attackStatus: 'BLOCKED',
    attackTotal: 1000
  }),
  // Application without vulnerabilities
  buildMockApp({
    name: 'NoVulns',
    vulnerabilityBreakdown: buildVulnBreakdownVariant(
      VULNERABILITY_STATUSES.map(() => 0)
    )
  }),
  // Application without critical vulnerabilities
  buildMockApp({
    name: 'NoCriticalVulns',
    vulnerabilityBreakdown: buildVulnBreakdownVariant([0, 1, 1, 1, 1])
  }),
  // Application with one vulnerability per severity
  buildMockApp({
    name: 'OneEachVulns',
    vulnerabilityBreakdown: buildVulnBreakdownVariant(
      VULNERABILITY_SEVERITIES.map(() => 1)
    )
  }),
  /**
   * Specific critical counts, to account for variant warning badge widths
   */
  buildMockApp({
    name: 'DoubleDigitCritical',
    vulnerabilityBreakdown: buildVulnBreakdownVariant([99, 0, 0, 0, 0])
  }),
  buildMockApp({
    name: 'TripleDigitCritical',
    vulnerabilityBreakdown: buildVulnBreakdownVariant([999, 0, 0, 0, 0])
  }),
  buildMockApp({
    name: 'QuadrupleDigitCritical',
    vulnerabilityBreakdown: buildVulnBreakdownVariant([9999, 0, 0, 0, 0])
  }),
  /**
   * Long application names
   */
  buildMockApp({
    name: 'LongNameExpectTruncation000'
  }),
  buildMockApp({
    name: 'Long Name Expect Truncation 001'
  }),
  buildMockApp({
    name: 'Test Truncation',
    technologies: ['Spring', 'Hibernate']
  }),
  buildMockApp({
    name: 'Test Truncation Long Text',
    technologies: ['Spring', 'Hibernate']
  }),
  buildMockApp({
    name: 'Test Truncation Long Text Long Text',
    technologies: ['Spring', 'Hibernate']
  }),
  buildMockApp({
    name: 'Test Truncation Long Text Long Text Long Text',
    technologies: ['Spring', 'Hibernate']
  }),
  buildMockApp({
    name: 'Test Truncation Long Text Long Text Long Text Long Text',
    technologies: ['Spring', 'Hibernate']
  }),
  buildMockApp({
    name: 'Test Truncation Long Text Long Text Long Text Long Text Long Text',
    technologies: ['Spring', 'Hibernate']
  }),
  buildMockApp({
    name: 'Test Truncation Long Text Long Text Long Text Long Text Long Text Long Text',
    technologies: ['Spring', 'Hibernate']
  }),
  buildMockApp({
    name: 'Test Truncation Long Text Long Text Long Text Long Text Long Text Long Text',
    technologies: ['Spring', 'Hibernate'],
    primary: true
  }),
  buildMockApp({
    name: 'Test Truncation',
    license: {
      expiration: '1975-08-19T23:15:30.000Z'
    },
    technologies: ['Spring', 'Hibernate']
  }),
  buildMockApp({
    name: 'Test Truncation Long Text',
    license: {
      expiration: '1975-08-19T23:15:30.000Z'
    },
    technologies: ['Spring', 'Hibernate']
  }),
  buildMockApp({
    name: 'Test Truncation Long Text Long Text',
    license: {
      expiration: '1975-08-19T23:15:30.000Z'
    },
    technologies: ['Spring', 'Hibernate']
  }),
  buildMockApp({
    name: 'Test Truncation Long Text Long Text Long Text',
    license: {
      expiration: '1975-08-19T23:15:30.000Z'
    },
    technologies: ['Spring', 'Hibernate']
  }),
  buildMockApp({
    name: 'Test Truncation Long Text Long Text Long Text Long Text',
    license: {
      expiration: '1975-08-19T23:15:30.000Z'
    },
    technologies: ['Spring', 'Hibernate']
  }),
  buildMockApp({
    name: 'Test Truncation Long Text Long Text Long Text Long Text Long Text',
    license: {
      expiration: '1975-08-19T23:15:30.000Z'
    },
    technologies: ['Spring', 'Hibernate']
  }),
  buildMockApp({
    name: 'Test Truncation Long Text Long Text Long Text Long Text Long Text Long Text',
    license: {
      expiration: '1975-08-19T23:15:30.000Z'
    },
    technologies: ['Spring', 'Hibernate']
  }),
  buildMockApp({
    name: 'Test Truncation Long Text Long Text Long Text Long Text Long Text Long Text',
    license: {
      expiration: '1975-08-19T23:15:30.000Z'
    },
    technologies: ['Spring', 'Hibernate'],
    primary: true
  }),
  /**
   * Vuln breakdown thermometer - variants of section widths
   */
  buildMockApp({
    name: 'VulnTherm000',
    vulnerabilityBreakdown: buildVulnBreakdownVariant([1, 8, 10, 12, 14])
  }),
  buildMockApp({
    name: 'VulnTherm001',
    vulnerabilityBreakdown: buildVulnBreakdownVariant([14, 12, 10, 8, 1])
  }),
  buildMockApp({
    name: 'VulnTherm002',
    vulnerabilityBreakdown: buildVulnBreakdownVariant([1, 1, 1, 1, 9999])
  }),
  buildMockApp({
    name: 'VulnTherm003',
    vulnerabilityBreakdown: buildVulnBreakdownVariant([1, 1, 1, 1, 999])
  }),
  buildMockApp({
    name: 'VulnTherm004',
    vulnerabilityBreakdown: buildVulnBreakdownVariant([1, 1, 1, 1, 99])
  }),
  /**
   * Specific metadata application values
   */
  buildMockApp({
    name: 'Specific metadata app',
    url: 'http://app.metadata.com',
    linesOfCode: '94300',
    importance: 'MEDIUM',
    technologies: ['Spring', 'Hibernate']
  }),
  buildMockApp({
    name: 'Online Aplication',
    online: true
  }),
  buildMockApp({
    name: 'Primary Aplication',
    primary: true
  }),
  buildMockApp({
    name: 'Assess not supported (Python)',
    language: AgentLanguageEnum.PYTHON
  }),
  buildMockApp({
    name: 'Compliance Policy Violation',
    violatedCompliancePolicies: [{ id: '1', name: 'OWASP 2015', enabled: true }]
  }),
  buildMockApp({
    name: 'Compliance Policy Violation (multiple)',
    violatedCompliancePolicies: [
      { id: '1', name: 'OWASP 2015', enabled: true },
      { id: '2', name: 'DISA', enabled: true }
    ]
  }),
  // Custom Metadata
  buildMockApp({
    name: 'Custom Metadata',
    customMetadataFields: [
      {
        name: 'Team',
        value: 'Developers',
        unique: true,
        type: 'STRING',
        subfields: undefined,
        isOverriddenByUser: false
      }
    ]
  }),
  buildMockApp({
    name: 'Custom Metadata (multiple)',
    customMetadataFields: [
      {
        name: 'Team',
        value: 'Developers',
        unique: true,
        type: 'STRING',
        subfields: undefined,
        isOverriddenByUser: false
      },
      {
        name: 'Area',
        value: '3',
        unique: true,
        type: 'STRING',
        subfields: undefined,
        isOverriddenByUser: false
      },
      {
        name: 'Variant',
        value: 'Release',
        unique: true,
        type: 'STRING',
        subfields: undefined,
        isOverriddenByUser: false
      }
    ]
  }),
  buildMockApp({
    name: 'Custom Metadata (POC)',
    customMetadataFields: [
      {
        name: 'poc',
        value: '',
        unique: true,
        type: 'PERSON_OF_CONTACT',
        subfields: [
          {
            name: 'name',
            value: 'cPerez',
            unique: true,
            type: 'CONTACT_NAME',
            isOverriddenByUser: false
          },
          {
            name: 'email',
            value: 'cPerez@email.com',
            unique: true,
            type: 'EMAIL',
            isOverriddenByUser: false
          },
          {
            name: 'phone',
            value: '2721234567',
            unique: true,
            type: 'PHONE',
            isOverriddenByUser: false
          }
        ],
        isOverriddenByUser: false
      }
    ]
  }),
  // Tags
  buildMockApp({
    name: 'Test Truncation',
    technologies: ['Spring', 'Hibernate'],
    tags: ['apples'],
    license: {
      expiration: '1975-08-19T23:15:30.000Z'
    }
  }),
  buildMockApp({
    name: 'Test Truncation Long Text',
    technologies: ['Spring', 'Hibernate'],
    tags: ['apples'],
    license: {
      expiration: '1975-08-19T23:15:30.000Z'
    }
  }),
  buildMockApp({
    name: 'Test Truncation Long Text Long Text',
    technologies: ['Spring', 'Hibernate'],
    tags: ['apples'],
    license: {
      expiration: '1975-08-19T23:15:30.000Z'
    }
  }),
  buildMockApp({
    name: 'Test Truncation Long Text Long Text Long Text',
    technologies: ['Spring', 'Hibernate'],
    tags: ['apples'],
    license: {
      expiration: '1975-08-19T23:15:30.000Z'
    }
  }),
  buildMockApp({
    name: 'Test Truncation',
    technologies: ['Spring', 'Hibernate'],
    tags: ['oranges', 'a really long long long long tag', 'foo'],
    license: {
      expiration: '1975-08-19T23:15:30.000Z'
    }
  }),
  buildMockApp({
    name: 'Test Truncation Long Text',
    technologies: ['Spring', 'Hibernate'],
    tags: ['apples', 'oranges', 'a really long long long long tag'],
    license: {
      expiration: '1975-08-19T23:15:30.000Z'
    }
  }),
  buildMockApp({
    name: 'Test Truncation Long Text Long Text',
    technologies: ['Spring', 'Hibernate'],
    tags: ['apples'],
    license: {
      expiration: '1975-08-19T23:15:30.000Z'
    }
  }),
  buildMockApp({
    name: 'Test Truncation Long Text Long Text Long Text',
    technologies: ['Spring', 'Hibernate'],
    tags: ['apples'],
    license: {
      expiration: '1975-08-19T23:15:30.000Z'
    }
  })
]
